import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Monitor, Printer, Wifi, WifiOff, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  settingsService,
  type PrintAgentInfo,
} from '../../services/httpServices/settingsService';
import {
  PRINT_AGENT_OFFLINE_EVENT,
  type PrintAgentOfflineDetail,
} from '../../utils/printAgentOffline';
import { agentMatchesPrinter, normalizeHostKey } from '../../utils/printPresence';

/**
 * Shown when a print was queued for another PC but no receiver was connected at
 * the time. The job is **not lost** — the backend keeps it `pending` and delivers
 * it as soon as the printer's PC comes back online — so this modal explains the
 * real state and how to bring the PC back, using the same signals the delivery
 * path does:
 *
 *  - an **enrolled print agent** on the printer's PC (the modern receiver; it
 *    reconnects on its own and flushes queued jobs), and
 *  - a **dashboard** open on that PC (the legacy receiver).
 */
export function RemotePrintOfflineModal() {
  const [detail, setDetail] = useState<PrintAgentOfflineDetail | null>(null);
  const [agents, setAgents] = useState<PrintAgentInfo[] | null>(null);
  const [connected, setConnected] = useState<string[] | null>(null);

  useEffect(() => {
    const onOffline = (event: Event) => {
      setDetail((event as CustomEvent<PrintAgentOfflineDetail>).detail);
    };
    window.addEventListener(PRINT_AGENT_OFFLINE_EVENT, onOffline as EventListener);
    return () => window.removeEventListener(PRINT_AGENT_OFFLINE_EVENT, onOffline as EventListener);
  }, []);

  // Fetch the live presence of both receivers so the user can see exactly which
  // one is missing and compare the target host with what is actually connected.
  useEffect(() => {
    if (!detail) return;
    let cancelled = false;
    setAgents(null);
    setConnected(null);

    settingsService
      .listPrintClients()
      .then((res) => {
        if (!cancelled) setConnected(res.data?.identities || []);
      })
      .catch(() => {
        if (!cancelled) setConnected([]);
      });

    settingsService
      .listPrintAgents()
      .then((res) => {
        if (!cancelled) setAgents(res.data?.agents || []);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      });

    return () => {
      cancelled = true;
    };
  }, [detail]);

  if (!detail) return null;

  const host = detail.host || 'the printer PC';
  const printerName = detail.printerName || detail.shareName || '';
  const hostKey = normalizeHostKey(host);
  const loading = agents === null || connected === null;

  const knownAgents = (agents || []).filter((agent) => agent.enabled);
  const matchingAgents = knownAgents.filter((agent) =>
    agentMatchesPrinter(agent, host, detail.shareName),
  );
  const onlineMatch = matchingAgents.find((agent) => agent.online);
  const offlineMatch = matchingAgents.find((agent) => !agent.online);
  const agentOnline = Boolean(onlineMatch);
  const dashboardOnline = (connected || []).some((id) => normalizeHostKey(id) === hostKey);
  const targetOnline = agentOnline || dashboardOnline;

  const onlineAgents = knownAgents.filter((agent) => agent.online);
  const noMatchingAgent = !loading && matchingAgents.length === 0;
  // If the target host matches neither a known agent nor a connected dashboard,
  // the stored PC name / IP is the likely culprit.
  const hostLikelyStale = noMatchingAgent && (onlineAgents.length > 0 || (connected || []).length > 0);

  const lastSeen = (agent: PrintAgentInfo) =>
    agent.lastSeenAt ? new Date(agent.lastSeenAt).toLocaleString() : 'unknown';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-warning/10 shrink-0">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-text">Printer PC is not connected</h3>
              <p className="text-xs text-text-muted mt-0.5">
                {printerName ? `The job for ${printerName} was queued.` : 'The job was queued.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setDetail(null)}
            className="p-1 rounded hover:bg-background text-text-muted"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm text-text-muted">
          <p>
            {targetOnline ? (
              <>
                A receiver for <strong className="text-text">{host}</strong> just came back online.
              </>
            ) : (
              <>
                No print receiver is connected on <strong className="text-text">{host}</strong> right
                now.
              </>
            )}{' '}
            The job is saved and prints automatically as soon as that PC reconnects — you can close
            this and keep working.
          </p>

          {/* Live receiver status — reveals which signal is missing or a stale host. */}
          <div className="rounded-lg border border-border bg-background p-3 space-y-2">
            {loading ? (
              <p className="flex items-center gap-2 font-medium text-text">
                Checking print receivers…
              </p>
            ) : (
              <>
                <p className="flex items-center gap-2 font-medium text-text">
                  {agentOnline ? (
                    <>
                      <Wifi size={15} className="text-success" /> Print agent online
                    </>
                  ) : offlineMatch ? (
                    <>
                      <WifiOff size={15} className="text-danger" /> Print agent offline
                    </>
                  ) : (
                    <>
                      <WifiOff size={15} className="text-warning" /> No print agent found for this
                      PC
                    </>
                  )}
                </p>
                <ul className="space-y-1 text-xs">
                  <li>
                    {agentOnline
                      ? `Agent ${onlineMatch?.hostname || ''} is connected and will print queued jobs.`
                      : offlineMatch
                        ? `Last seen ${lastSeen(offlineMatch)}. The job will print when it reconnects.`
                        : 'No enrolled agent advertises this printer\u2019s share or host.'}
                  </li>
                  <li className="flex items-center gap-1.5">
                    {dashboardOnline ? (
                      <>
                        <CheckCircle2 size={12} className="text-success" /> A dashboard is open on{' '}
                        {host}.
                      </>
                    ) : (
                      <>
                        <WifiOff size={12} className="text-text-muted" /> No dashboard is open on{' '}
                        {host}.
                      </>
                    )}
                  </li>
                </ul>
              </>
            )}

            {!loading && (onlineAgents.length > 0 || (connected || []).length > 0) && (
              <div className="pt-1 border-t border-border space-y-1 text-xs">
                <p className="font-medium text-text">Connected now</p>
                <ul className="space-y-0.5">
                  {onlineAgents.map((agent) => (
                    <li key={`agent-${agent.id}`}>
                      <code className="bg-surface px-1 rounded">{agent.hostname || agent.id}</code>
                      <span className="ml-2 text-success">print agent</span>
                    </li>
                  ))}
                  {(connected || []).map((id) => (
                    <li key={`dash-${id}`}>
                      <code className="bg-surface px-1 rounded">{id}</code>
                      <span className="ml-2 text-text-muted">dashboard</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hostLikelyStale && (
              <p className="text-xs text-warning pt-1">
                This job targeted <strong>{host}</strong>, which matches neither a connected agent
                nor a dashboard — the printer's PC name or IP has probably changed. Edit the device
                under Settings → Devices and set its host to a value from the list above.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border bg-background p-3 space-y-2">
            <p className="flex items-center gap-2 font-medium text-text">
              <Monitor size={15} /> On {host}
            </p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>
                Make sure the <strong>Fast Aid Print Agent</strong> is installed and running. It
                reconnects on its own and prints queued jobs — no browser tab needed.
              </li>
              <li>
                Confirm the printer
                {printerName ? ` "${printerName}"` : ''} is shared in Windows and that this device's
                share name / host match it.
              </li>
              <li>
                As a fallback, keeping a Fast Aid OS dashboard open on that PC (signed in) also
                receives jobs.
              </li>
            </ol>
          </div>

          <p className="flex items-start gap-2 text-xs">
            <Printer size={14} className="mt-0.5 shrink-0" />
            Once the PC reconnects, the queued job prints automatically.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <Button onClick={() => setDetail(null)}>Got it</Button>
        </div>
      </div>
    </div>
  );
}
